import { Component, OnInit, inject, effect, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { Backend } from "../../backend-interface/backend";
import { Attestation, BwFilters, BwObjectPromise } from "../../service/global.service";
import { DatePipe } from "@angular/common";
import { ToastService } from 'src/app/service/toast.service';
import { HttpErrorResponse } from '@angular/common/http';
import { HasRolesDirective } from 'keycloak-angular';
import { TranslationService } from 'src/app/service/translation.service';
import { forkJoin } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { DatepickerComponent } from 'src/app/components/datepicker/datepicker.component';
import { FiltersACComponent } from 'src/app/components/filters/filters-ac/filters-ac.component';
import { NgbPaginationModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { LoadingErrorComponent } from 'src/app/components/loading-error/loading-error.component';
import { DetailsAttestationsWithoutScoreComponent } from './details-attestations-without-score/details-attestations-without-score.component';
import { PreventionalScoreComponent } from './preventional-score/preventional-score.component';
import { AttestationModalComponent } from './attestation-modal/attestation-modal.component';
import { ToastComponent } from 'src/app/components/toast/toast.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ExistObjectPipe } from 'src/app/pipes/filter.pipe';
import { AccessManager, database } from 'src/app/acces_manager';

@Component({
  selector: 'app-liste-attestations',
  templateUrl: './liste-attestations.component.html',
  styleUrls: ['./liste-attestations.component.scss'],
  standalone: true,
  providers: [DatePipe, ToastService, TranslationService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HasRolesDirective,
    TranslateModule,
    DatepickerComponent,
    FiltersACComponent,
    NgbPaginationModule,
    LoadingErrorComponent,
    DetailsAttestationsWithoutScoreComponent,
    PreventionalScoreComponent,
    AttestationModalComponent,
    NgbTooltipModule,
    ToastComponent,
    MatProgressSpinnerModule,
    ExistObjectPipe
  ]
})
export class ListeAttestationsComponent implements OnInit {
  readonly database: AccessManager = database;
  // Signals for state
  listAll = signal<Attestation[]>([]);
  listBorn = signal<Attestation[]>([]);
  loadingOerror = signal(true);
  promiseResult?: BwObjectPromise;
  filtersActive = signal<BwFilters>({ pageSize: 50 });
  searchmodel = signal<string>('');
  attesCourante = signal<Attestation | undefined>(undefined);
  page = signal(1);
  pageSize = signal(this.filtersActive().pageSize ?? 50);

  // Static lists
  readonly listeNomsFamiliesIOS = [
    'utils', 'behavior', 'debugger', 'virtualisation', 'memory', 'jailbreak', 'hook', 'integrity', 'status', 'sensitive', 'black&White', 'hardware', 'software', 'sensors'
  ];
  readonly listeNomsFamiliesAndroid = [
    'utils', 'behavior', 'debugger', 'virtualisation', 'memory', 'root', 'hook', 'integrity', 'status', 'sensitive', 'black&White', 'hardware', 'software', 'sensors'
  ];

  predId = '';
  scoreMin = 0;
  scoreMax = 100;
  selectedOS = "OS";

  // Services
  readonly translationService = inject(TranslationService);
  readonly toastService = inject(ToastService);
  readonly datepipe = inject(DatePipe);

  readonly dateRangeTitle = computed(() => {
    const filters = this.filtersActive();
    if (filters.from && filters.to) {
      return this.titrefiltreDate(filters.from, filters.to).toLowerCase();
    }
    return '';
  });

  readonly attestationDateRange = computed(() => {
    const born = this.listBorn();
    if (born.length > 0) {
      return `${this.datepipe.transform(born[born.length - 1].createdAt, this.translationService.getDateFormat())} - ${this.datepipe.transform(born[0].createdAt, this.translationService.getDateFormat())}`;
    }
    return '';
  });

  constructor() {
    effect(() => {
      forkJoin({
        menuAttestations: this.translationService.get('menu-attestations'),
        nav: this.translationService.get('attestations-nav-report-list'),
        list: this.translationService.get('list')
      }).subscribe(({ menuAttestations, nav, list }) => {
        document.title = `DIP - ${menuAttestations} - ${nav} ${list}`;
      });
    });
  }

  ngOnInit(): void {
    this.getDefaultList();
  }

  async getDefaultList(): Promise<void> {
    const savedFilters = this.database.getdata('attestationsFilters');
    if (savedFilters) {
      this.filtersActive.set(savedFilters);
      await this.searchRequestFilters(this.filtersActive());
    } else {
      await this.searchResultWithTimeStamp();
      await this.loading(0.5);
    }
  }

  async loading(seconds: number): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, seconds * 1000));
    this.loadingOerror.set(false);
  }

  async searchAttestations(filters: BwFilters): Promise<BwObjectPromise> {
    return Backend.ATTESTATIONS.search(filters);
  }

  async searchResultWithTimeStamp(): Promise<void> {
    const timeIntervals = [86400000, 604800000, 1209600000];
    const filters = { ...this.filtersActive(), to: Date.now() };
    for (const interval of timeIntervals) {
      filters.from = filters.to - interval;
      this.promiseResult = await this.searchAttestations(filters);
      if (this.promiseResult.results) {
        this.listBorn.set(JSON.parse(this.promiseResult.results));
        this.filtersActive.set(filters);
        return;
      }
    }
    await this.handleNoResults();
  }

  async handleNoResults(): Promise<void> {
    const filters = { ...this.filtersActive(), from: undefined, to: undefined };
    this.filtersActive.set(filters);
    await this.getAllAttestations();
    this.listBorn.set(this.listAll());
    if (this.listBorn().length > 0) {
      this.filtersActive.set({ ...filters, from: 1577833200000, to: this.listBorn()[0].createdAt });
    }
  }

  async resetAll(): Promise<void> {
    this.filtersActive.set({ pageSize: 50 });
    this.loadingOerror.set(true);
    this.searchmodel.set('');
    await this.searchResultWithTimeStamp();
    await this.loading(0.15);
    this.database.setdata('attestationsFilters', this.filtersActive());
  }

  async getAllAttestations(): Promise<void> {
    this.promiseResult = await this.searchAttestations(this.filtersActive());
    this.listAll.set(this.promiseResult.results ? JSON.parse(this.promiseResult.results) : []);
  }

  async searchByTimestamp([from, to]: [number, number]): Promise<void> {
    const filters = { ...this.filtersActive(), from, to };
    this.filtersActive.set(filters);
    this.patchMainFilters();
    this.searchmodel.set('');
    this.loadingOerror.set(true);
    this.page.set(1);
    this.promiseResult = await this.searchAttestations(this.filtersActive());
    this.patchCurrentList(this.promiseResult);
    this.database.setdata('attestationsFilters', this.filtersActive());
    this.unsetAttestationSelected();
    await this.loading(0.15);
  }

  async nextPage(page: number): Promise<void> {
    this.loadingOerror.set(true);
    this.filtersActive.set({ ...this.filtersActive(), page });
    this.page.set(page);
    this.promiseResult = await this.searchAttestations(this.filtersActive());
    this.patchCurrentList(this.promiseResult);
    this.unsetAttestationSelected();
    await this.loading(0.15);
  }

  patchCurrentList(objectA: BwObjectPromise): void {
    this.listBorn.set(objectA.results ? JSON.parse(objectA.results) : []);
  }

  async filtreAppNamefromAll(nom: string): Promise<void> {
    this.loadingOerror.set(true);
    this.promiseResult = await Backend.BW_FILTRES.filtreAppFromAll(this.promiseResult, nom, 'attes');
    if (this.promiseResult) {
      this.patchCurrentList(this.promiseResult);
      this.page.set(1);
      const savedFilters = this.database.getdata('attestationsFilters');
      if (savedFilters) {
        this.filtersActive.set(savedFilters);
      }
      this.loadingOerror.set(false);
    }
  }

  awaitChargementListe(): boolean {
    return this.listBorn().length > 0;
  }

  setAttestationSelectionnee(attestation: Attestation): void {
    this.attesCourante.set(attestation);
  }

  unsetAttestationSelected(): void {
    this.attesCourante.set(undefined);
  }

  scroolTo(): void {
    document.getElementById('scoreAtt')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  getAllFamilies(): string[] {
    const platform = this.attesCourante()?.deviceInfo?.platformName?.toLowerCase();
    if (platform === "android") return this.listeNomsFamiliesAndroid;
    if (platform === "ios") return this.listeNomsFamiliesIOS;
    return [];
  }

  today(): number {
    return Date.now();
  }

  titrefiltreDate(debut: number, fin: number): string {
    return Backend.FORMAT.formatDate(debut, fin);
  }

  formatLongString(chaine: string | undefined, n: number): string {
    return Backend.FORMAT.formatN(chaine ?? '', n) ?? '';
  }

  formatVersion(version: string): string {
    return version.length > 7 ? version.slice(0, 7) : version;
  }

  formatId(id: string): string {
    return Backend.FORMAT.formatId(id);
  }

  majFormID(event: Event, id: string): string {
    return event ? this.formatId(id) : id;
  }

  formatVersionFiltre(version: string): string {
    return Backend.FORMAT.formatVersionCheckBox(version);
  }

  formatAPPName(appName: string): string {
    return Backend.FORMAT.formatAPPName(appName);
  }

  scoreInAttestations(): boolean {
    return !!(this.listBorn().some(elem => elem.score_prevention));
  }

  patchMainFilters(filters?: BwFilters): void {
    this.filtersActive.set(Backend.BW_FILTRES.patchMainFilters(this.filtersActive(), filters));
  }

  patchSearchModel(event: BwFilters): void {
    this.searchmodel.set(event.bwAppId || event.id || event.deviceId || event.metadata || '');
  }

  formatName(model: string) {
    return Backend.FORMAT.formatName(model)
  }

  async searchRequestFilters(event: BwFilters): Promise<void> {
    this.loadingOerror.set(true);
    this.patchSearchModel(event);
    this.patchMainFilters(event);
    try {
      this.promiseResult = await this.searchAttestations(this.filtersActive());
      this.patchCurrentList(this.promiseResult);
      this.database.setdata('attestationsFilters', this.filtersActive());
      await this.loading(0.5);
    } catch (e) {
      const err = e as HttpErrorResponse;
      this.toastService.show(err.error?.message ?? 'Erreur inconnue', { classname: 'bg-danger', delay: 6000 });
      await this.resetAll();
    }
    this.unsetAttestationSelected();
  }
}
